import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, CheckCircle2, KeyRound, Loader2, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { toast } from "@/hooks/use-toast";

type VerifyRow = {
  table_name: string;
  encrypted_rows: number;
  decrypt_ok: number;
};

type HealthState =
  | { status: "loading" }
  | { status: "hidden" }
  | { status: "healthy"; total: number }
  | { status: "empty" }
  | { status: "degraded"; failures: Array<{ table: string; failed: number }>; total: number };

const TABLE_LABELS: Record<string, string> = {
  content_sources: "Source library",
  excerpts: "Excerpts",
  content_briefs: "Briefs",
  content_avatars: "Avatars",
  content_voice_profiles: "Voice profiles",
  authors: "Authors",
  library_items: "Library items",
};

/**
 * Static informational card that explains which content categories are
 * encrypted at rest using a per-workspace key, plus a live "Encryption
 * health" line that calls verify_decrypt to confirm every encrypted row
 * round-trips cleanly.
 */
export const DataAtRestCard = () => {
  const { active } = useWorkspace();
  const [health, setHealth] = useState<HealthState>({ status: "loading" });
  const [rotating, setRotating] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null));
  }, []);

  const isOwner = !!(active && currentUserId && active.owner_user_id === currentUserId);

  const loadHealth = useCallback(async () => {
    if (!active) {
      setHealth({ status: "loading" });
      return;
    }
    setHealth({ status: "loading" });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any).rpc("verify_decrypt", {
      _workspace_id: active.id,
    });
    if (error) {
      setHealth({ status: "hidden" });
      return;
    }
    const rows = (data ?? []) as VerifyRow[];
    const total = rows.reduce((sum, row) => sum + Number(row.encrypted_rows ?? 0), 0);
    const failures = rows
      .map((row) => ({
        table: row.table_name,
        failed: Number(row.encrypted_rows ?? 0) - Number(row.decrypt_ok ?? 0),
      }))
      .filter((row) => row.failed > 0);

    if (failures.length > 0) {
      setHealth({ status: "degraded", failures, total });
    } else if (total === 0) {
      setHealth({ status: "empty" });
    } else {
      setHealth({ status: "healthy", total });
    }
  }, [active]);

  useEffect(() => {
    void loadHealth();
  }, [loadHealth]);

  const handleRotate = useCallback(async () => {
    if (!active) return;
    setRotating(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any).rpc("rotate_workspace_pepper", {
      _workspace_id: active.id,
    });
    setRotating(false);
    if (error) {
      toast({
        title: "Rotation failed",
        description: error.message ?? "Could not rotate the encryption key.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Encryption key rotated",
      description: "All encrypted records were re-encrypted with a new key.",
    });
    await loadHealth();
  }, [active, loadHealth]);

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Privacy &amp; data at rest
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground">
        <p>
          The free-text content you write inside Lovable is encrypted at rest
          in our database using a key derived per workspace. Even if a
          database backup leaks, the protected fields stay unreadable
          without the key.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Excerpt notes</li>
          <li>Library item notes &amp; descriptions</li>
          <li>Author notes</li>
          <li>Composer source summaries &amp; notes</li>
          <li>Brief notes &amp; prompts</li>
          <li>Generated drafts and prepared publications</li>
          <li>Audience avatars and voice profiles</li>
        </ul>
        <p className="text-xs">
          Excerpt body text and item titles are kept in plaintext so search
          and listing remain fast. Authentication credentials use a
          separate, dedicated encryption key.
        </p>

        {health.status !== "hidden" ? (
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-foreground">
              Encryption health
            </p>
            {health.status === "loading" ? (
              <Skeleton className="h-4 w-48" />
            ) : health.status === "empty" ? (
              <p className="text-xs">
                No encrypted records in this workspace yet.
              </p>
            ) : health.status === "healthy" ? (
              <p className="flex items-center gap-2 text-xs text-foreground">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                All {health.total} encrypted record{health.total === 1 ? "" : "s"} decrypt cleanly.
              </p>
            ) : (
              <div className="space-y-1 text-xs">
                <p className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                  Some records failed to decrypt. Contact support if this persists.
                </p>
                <ul className="list-disc pl-5">
                  {health.failures.map((row) => (
                    <li key={row.table}>
                      {TABLE_LABELS[row.table] ?? row.table}: {row.failed} failing
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : null}

        {isOwner && health.status !== "hidden" && health.status !== "degraded" ? (
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-foreground">
              Rotate encryption key
            </p>
            <p className="mb-2 text-xs">
              Generates a new per-workspace key and re-encrypts every protected
              record. Useful as a periodic hygiene step or after a suspected
              key exposure. Other workspaces are unaffected.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={rotating || health.status === "loading"}>
                  {rotating ? (
                    <>
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      Rotating…
                    </>
                  ) : (
                    <>
                      <KeyRound className="mr-2 h-3.5 w-3.5" />
                      Rotate key for this workspace
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Rotate encryption key?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Every encrypted record in <strong>{active?.workspace_label ?? active?.display_name ?? "this workspace"}</strong>{" "}
                    will be decrypted with the current key and re-encrypted with a
                    new one in a single transaction. Your data stays readable
                    throughout. This may take a few seconds for large workspaces.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRotate}>Rotate now</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
